import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";
import { useMember } from "../MemberContext";
import { ExerciseGrid } from "../ExerciseCard";
import { H1, Overline, PageLoader } from "../ui";

export const useFavToggle = (setItems) => useCallback(async (ex) => {
  try {
    const { favorite } = await api(`/me/favorites/${ex.exercise_id}`, { method: "POST" });
    setItems((list) => list.map((x) => (x.exercise_id === ex.exercise_id ? { ...x, favorite } : x)));
    toast.success(favorite ? "Zu Favoriten hinzugefügt" : "Aus Favoriten entfernt");
  } catch (e) { toast.error(e.message); }
}, [setItems]);

const Select = ({ value, onChange, options, placeholder, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
    className="h-11 px-3 bg-[#121212] border border-white/10 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#c1ff72]">
    <option value="">{placeholder}</option>
    {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
  </select>
);

function Filters({ meta, p, setP }) {
  const set = (k, v) => { const n = new URLSearchParams(p); if (v) n.set(k, v); else n.delete(k); setP(n, { replace: true }); };
  const any = ["kategorie", "niveau", "ort", "material", "q"].some((k) => p.get(k));
  return (
    <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 mb-8 [&>select]:w-full sm:[&>select]:w-auto" data-testid="library-filters">
      <div className="relative col-span-2 sm:flex-1 sm:min-w-[200px]"><Search className="w-4 h-4 absolute left-3 top-3.5 text-[#71717A]" />
        <input value={p.get("q") || ""} onChange={(e) => set("q", e.target.value)} placeholder="Übung suchen…" data-testid="library-search"
          className="w-full h-11 pl-9 pr-3 bg-[#121212] border border-white/10 text-sm text-white placeholder:text-[#71717A] focus:outline-none focus:ring-2 focus:ring-[#c1ff72]" /></div>
      <Select value={p.get("kategorie") || ""} onChange={(v) => set("kategorie", v)} options={meta.categories} placeholder="Alle Kategorien" testId="filter-category" />
      <Select value={p.get("niveau") || ""} onChange={(v) => set("niveau", v)} options={meta.levels} placeholder="Alle Niveaus" testId="filter-level" />
      <Select value={p.get("ort") || ""} onChange={(v) => set("ort", v)} options={meta.locations} placeholder="Alle Orte" testId="filter-location" />
      <Select value={p.get("material") || ""} onChange={(v) => set("material", v)} options={[{ id: "ohne", label: "Ohne Equipment" }, { id: "mein", label: "Mit meinem Equipment" }, { id: "klein", label: "Für kleine Räume" }]} placeholder="Jedes Material" testId="filter-equipment" />
      {any && <button onClick={() => setP(new URLSearchParams(), { replace: true })} className="h-11 px-4 flex items-center gap-1 text-sm text-[#A1A1AA] hover:text-white" data-testid="filter-reset"><X className="w-4 h-4" />Zurücksetzen</button>}
    </div>
  );
}

export default function Library() {
  const { meta, member } = useMember();
  const [p, setP] = useSearchParams();
  const [items, setItems] = useState(null);
  const onFav = useFavToggle(setItems);
  useEffect(() => {
    const q = new URLSearchParams();
    const map = { kategorie: "category", niveau: "level", ort: "location", q: "q" };
    Object.entries(map).forEach(([k, v]) => p.get(k) && q.set(v, p.get(k)));
    if (p.get("material") === "ohne") q.set("equipment", "ohne");
    if (p.get("material") === "klein") q.set("small_space", "true");
    if (p.get("material") === "mein") q.set("equipment", (member.equipment || []).join(",") || "keins");
    const t = setTimeout(() => api(`/exercises?${q}`).then((d) => setItems(d.items)).catch((e) => toast.error(e.message)), 200);
    return () => clearTimeout(t);
  }, [p, member]);
  if (!meta) return <PageLoader />;
  return (
    <div data-testid="library-page">
      <Overline className="mb-3">Trainingsbibliothek</Overline>
      <H1 className="mb-8 !text-5xl sm:!text-6xl">Alle Übungen</H1>
      <Filters meta={meta} p={p} setP={setP} />
      {!items ? <PageLoader /> : items.length ? (
        <><p className="text-xs uppercase tracking-wider text-[#71717A] mb-4" data-testid="library-count">{items.length} Übungen</p><ExerciseGrid items={items} onFav={onFav} testId="library-grid" /></>
      ) : <p className="text-[#71717A] py-16" data-testid="library-empty">Keine Übungen für diese Auswahl gefunden.</p>}
    </div>
  );
}
